import { logger } from "@/lib/logger";
import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Play, Download, AlertTriangle, CheckCircle2, FileText, Eye, Save, X, ChevronRight, ChevronDown } from "lucide-react";
import {
  useReconciliationRuns,
  useReconciliationFindings,
  useRunReconciliation,
  usePreviewReconciliation,
  useUpdateFindingStatus,
  findingTypeLabel,
  type ReconciliationRun,
  type ReconciliationPreview,
} from "@/hooks/useBillingReconciliation";
import { FindingExplanation } from "./FindingExplanation";
import { ReconciliationRevisionHistory } from "./ReconciliationRevisionHistory";



interface Props {
  trialSiteId?: string; // when set, scope to this site
  defaultTrialId?: string;
}

const fmtMoney = (n: number | null | undefined, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n ?? 0);

const severityVariant = (s: string): "default" | "destructive" | "secondary" =>
  s === "critical" ? "destructive" : s === "warning" ? "default" : "secondary";

export function BillingReconciliationPanel({ trialSiteId, defaultTrialId }: Props) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [trialId, setTrialId] = useState<string | undefined>(defaultTrialId);
  const [periodStart, setPeriodStart] = useState(firstOfMonth.toISOString().slice(0, 10));
  const [periodEnd, setPeriodEnd] = useState(today.toISOString().slice(0, 10));
  const [activeRunId, setActiveRunId] = useState<string | undefined>();
  const [filterType, setFilterType] = useState<string>("all");
  const [preview, setPreview] = useState<ReconciliationPreview | null>(null);

  // Trials the user can see
  const { data: trials } = useQuery({
    queryKey: ["recon-trials", trialSiteId],
    queryFn: async () => {
      if (trialSiteId) {
        const { data, error } = await supabase
          .from("trial_site_assignments")
          .select("trial_id, trials(id, title)")
          .eq("trial_site_id", trialSiteId);
        if (error) throw error;
        return (data ?? []).map((r: any) => r.trials).filter(Boolean);
      }
      const { data, error } = await supabase
        .from("trials")
        .select("id, title")
        .order("title")
        .limit(100);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: runs, isLoading: runsLoading } = useReconciliationRuns(trialId, trialSiteId);
  const { data: savedFindings, isLoading: findingsLoading } = useReconciliationFindings(activeRunId);

  const runMutation = useRunReconciliation();
  const previewMutation = usePreviewReconciliation();
  const updateFinding = useUpdateFindingStatus();

  const activeRun: ReconciliationRun | undefined = useMemo(
    () => runs?.find((r) => r.id === activeRunId) ?? runs?.[0],
    [runs, activeRunId]
  );

  // Unified "current view" — either preview (unsaved) or saved findings
  const isPreview = preview !== null;
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const currentFindings = useMemo(() => {
    if (isPreview) {
      return preview!.findings.map((f, idx) => ({
        ...f,
        id: `preview-${idx}`,
        run_id: "preview",
        status: "open" as const,
        resolution_notes: null,
        created_at: new Date().toISOString(),
      }));
    }
    return savedFindings ?? [];
  }, [isPreview, preview, savedFindings]);


  const visibleFindings = useMemo(() => {
    if (filterType === "all") return currentFindings;
    return currentFindings.filter((f) => f.finding_type === filterType);
  }, [currentFindings, filterType]);

  const onPreview = async () => {
    if (!trialId) return;
    const result = await previewMutation.mutateAsync({
      trial_id: trialId,
      trial_site_id: trialSiteId ?? null,
      period_start: periodStart,
      period_end: periodEnd,
    });
    setPreview(result);
    setActiveRunId(undefined);
  };

  const onSave = async () => {
    if (!trialId || !preview) return;
    const newRunId = await runMutation.mutateAsync({
      trial_id: trialId,
      trial_site_id: trialSiteId ?? null,
      period_start: preview.period_start,
      period_end: preview.period_end,
    });
    setPreview(null);
    setActiveRunId(newRunId);
  };

  const onDiscard = () => setPreview(null);


  const exportCsv = () => {
    const periodLabel = isPreview ? `${preview!.period_start}-to-${preview!.period_end}` : activeRun ? `${activeRun.period_start}-to-${activeRun.period_end}` : "current";
    if (!visibleFindings.length) return;

    const rows = [
      ["Finding type", "Severity", "Patient", "Expected", "Actual", "Variance", "Currency", "Status", "Description"],
      ...visibleFindings.map((f) => [
        findingTypeLabel(f.finding_type),
        f.severity,
        f.patient_id ?? "",
        f.expected_amount ?? "",
        f.actual_amount ?? "",
        f.variance_amount ?? "",
        f.currency,
        f.status,
        (f.description ?? "").replace(/[\r\n,]/g, " "),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `billing-accuracy-${periodLabel}${isPreview ? "-PREVIEW" : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    if (!activeRun) return;
    const { data, error } = await supabase.functions.invoke("generate-billing-accuracy-report", {
      body: { run_id: activeRun.id, originUrl: window.location.origin },
    });
    if (error) {
      logger.error(error);
      return;
    }
    if (data?.html) {
      const w = window.open("", "_blank");
      if (w) {
        w.document.write(data.html);
        w.document.close();
        w.print();
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Billing Reconciliation & Accuracy
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Controls */}
        <div className="grid gap-3 md:grid-cols-4">
          <div className="space-y-1.5">
            <Label>Trial</Label>
            <Select value={trialId} onValueChange={setTrialId}>
              <SelectTrigger><SelectValue placeholder="Select trial" /></SelectTrigger>
              <SelectContent>
                {trials?.map((t: any) => (
                  <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Period start</Label>
            <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Period end</Label>
            <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button onClick={onPreview} disabled={!trialId || previewMutation.isPending} className="w-full">
              {previewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              Preview deltas
            </Button>
          </div>

        </div>

        {/* Revision history */}
        <ReconciliationRevisionHistory
          runs={runs}
          activeRunId={activeRun?.id}
          onSelectRun={setActiveRunId}
        />
        {runsLoading && <div className="text-sm text-muted-foreground">Loading runs…</div>}
        {!runsLoading && !runs?.length && (
          <div className="text-sm text-muted-foreground">No runs yet. Choose a period and click Preview deltas.</div>
        )}

        {/* Preview banner */}
        {isPreview && preview && (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="space-y-1">
              <div className="flex items-center gap-2 font-medium text-amber-900 dark:text-amber-200">
                <Eye className="h-4 w-4" /> Preview — nothing has been saved yet
              </div>
              <div className="text-sm text-amber-800 dark:text-amber-300">
                {preview.findings.length} expected-vs-billed deltas detected for {preview.period_start} → {preview.period_end}. Review below, then save to persist findings to the audit trail.
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={onDiscard} disabled={runMutation.isPending}>
                <X className="h-4 w-4" /> Discard
              </Button>
              <Button size="sm" onClick={onSave} disabled={runMutation.isPending}>
                {runMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save findings
              </Button>
            </div>
          </div>
        )}

        {/* KPIs */}
        {(activeRun || isPreview) && (
          <>
            <div className="grid gap-3 md:grid-cols-5">
              <Kpi label="Accuracy" value={`${(isPreview ? preview!.accuracy_percent : activeRun!.accuracy_percent) ?? 0}%`} icon={<CheckCircle2 className="h-4 w-4" />} tone={((isPreview ? preview!.accuracy_percent : activeRun!.accuracy_percent) ?? 0) >= 95 ? "good" : "bad"} />
              <Kpi label="Over-billed" value={fmtMoney(isPreview ? preview!.over_billed_amount : activeRun!.over_billed_amount, isPreview ? preview!.currency : activeRun!.currency)} tone="bad" />
              <Kpi label="Under-billed" value={fmtMoney(isPreview ? preview!.under_billed_amount : activeRun!.under_billed_amount, isPreview ? preview!.currency : activeRun!.currency)} tone="bad" />
              <Kpi label="Amendment drift" value={fmtMoney(isPreview ? preview!.drift_amount : activeRun!.drift_amount, isPreview ? preview!.currency : activeRun!.currency)} tone="warn" />
              <Kpi label="Missed visits" value={String(isPreview ? preview!.missed_visit_count : activeRun!.missed_visit_count)} icon={<AlertTriangle className="h-4 w-4" />} tone="warn" />
            </div>


            {/* Findings */}
            <Tabs value={filterType} onValueChange={setFilterType}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <TabsList className="flex-wrap h-auto">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="phantom_billing">Phantom</TabsTrigger>
                  <TabsTrigger value="duplicate_billing">Duplicate</TabsTrigger>
                  <TabsTrigger value="amendment_drift">Drift</TabsTrigger>
                  <TabsTrigger value="missed_billable">Missed billable</TabsTrigger>
                  <TabsTrigger value="missed_scheduled_visit">Missed visit</TabsTrigger>
                  <TabsTrigger value="out_of_window_visit">Out of window</TabsTrigger>
                </TabsList>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={exportCsv} disabled={!visibleFindings.length}>
                    <Download className="h-4 w-4" /> CSV
                  </Button>
                  <Button size="sm" variant="outline" onClick={exportPdf} disabled={isPreview}>
                    <Download className="h-4 w-4" /> Accuracy report
                  </Button>
                </div>
              </div>
              <TabsContent value={filterType} className="mt-4">
                {!isPreview && findingsLoading ? (
                  <div className="text-sm text-muted-foreground">Loading findings…</div>

                ) : !visibleFindings.length ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">No findings in this category.</div>
                ) : (
                  <div className="border rounded-md overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Severity</TableHead>
                          <TableHead className="text-right">Expected</TableHead>
                          <TableHead className="text-right">Actual</TableHead>
                          <TableHead className="text-right">Variance</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleFindings.map((f) => {
                          const expanded = expandedRow === f.id;
                          const canExpand = !!(f as any).details;
                          return (
                            <Fragment key={f.id}>

                              <TableRow key={f.id}>
                                <TableCell className="p-1">
                                  {canExpand && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0"
                                      onClick={() => setExpandedRow(expanded ? null : f.id)}
                                      aria-label={expanded ? "Hide explanation" : "Show explanation"}
                                    >
                                      {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </Button>
                                  )}
                                </TableCell>
                                <TableCell>{findingTypeLabel(f.finding_type)}</TableCell>
                                <TableCell>
                                  <Badge variant={severityVariant(f.severity)}>{f.severity}</Badge>
                                </TableCell>
                                <TableCell className="text-right">{f.expected_amount != null ? fmtMoney(f.expected_amount, f.currency) : "—"}</TableCell>
                                <TableCell className="text-right">{f.actual_amount != null ? fmtMoney(f.actual_amount, f.currency) : "—"}</TableCell>
                                <TableCell className="text-right font-medium">{f.variance_amount != null ? fmtMoney(f.variance_amount, f.currency) : "—"}</TableCell>
                                <TableCell className="max-w-md text-sm text-muted-foreground">{f.description}</TableCell>
                                <TableCell>
                                  <Badge variant={f.status === "open" ? "default" : "secondary"}>{f.status}</Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  {isPreview ? (
                                    <span className="text-xs text-muted-foreground">Save to action</span>
                                  ) : f.status === "open" && (
                                    <div className="flex gap-1 justify-end">
                                      <Button size="sm" variant="ghost" onClick={() => updateFinding.mutate({ id: f.id, status: "resolved", resolution_notes: "Corrected accrual" })}>Resolve</Button>
                                      <Button size="sm" variant="ghost" onClick={() => updateFinding.mutate({ id: f.id, status: "waived", resolution_notes: "Waived by reviewer" })}>Waive</Button>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                              {expanded && (
                                <TableRow key={`${f.id}-detail`} className="bg-muted/20 hover:bg-muted/20">
                                  <TableCell></TableCell>
                                  <TableCell colSpan={8}>
                                    <FindingExplanation
                                      findingType={f.finding_type}
                                      details={(f as any).details}
                                      currency={f.currency}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>

                          );
                        })}
                      </TableBody>
                    </Table>

                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Kpi({ label, value, icon, tone }: { label: string; value: string; icon?: React.ReactNode; tone?: "good" | "bad" | "warn" }) {
  const toneClass =
    tone === "good" ? "text-emerald-600" :
    tone === "bad" ? "text-destructive" :
    tone === "warn" ? "text-amber-600" : "";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{label}</span>
          {icon}
        </div>
        <div className={`text-xl font-bold mt-1 ${toneClass}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
